import { timelockDecrypt, timelockEncrypt, Buffer } from "tlock-js";
import { decodeArmor, encodeArmor } from "tlock-js/age/armor.js";
import { finalizeEvent, generateSecretKey, getPublicKey, type Event, type VerifiedEvent } from "nostr-tools/pure";
import { createRumor, createSeal, unwrapEvent } from "nostr-tools/nip59";
import { encrypt as nip44Encrypt, getConversationKey } from "nostr-tools/nip44";
import { base64 } from "@scure/base";
import type { ChainClient } from "drand-client";
import { QUICKNET_CHAIN_HASH, currentRound, timeForRound } from "./drand.js";
import { isValidPublicKey } from "./keys.js";
import { senderTags, type SenderSnapshot } from "./sender.js";
import { MAX_FILES, ManifestError, PAYLOAD_MANIFEST_V1, encodeManifest, parseManifest, type ManifestFile } from "./manifest.js";
import {
  CapsuleError,
  KIND_GIFT_WRAP,
  KIND_TIME_CAPSULE,
  MAX_PLAINTEXT_BYTES,
  MAX_PRIVATE_PLAINTEXT_BYTES,
  assertRound,
  assertIntro,
  assertSubject,
  normalizeIntro,
  normalizeSubject,
  readCapsuleSender,
  readCapsuleTag,
  readDiscreet,
  readIntro,
  readPublicHeader,
  readPayload,
  readRecipients,
  readReplyTo,
  readSase,
  readSubject,
  readWrapHeader,
  verifyAuthor,
  type CapsuleHeader,
  type NamedKey,
  type Payload,
  type ReplyEnvelope,
} from "./validate.js";

export {
  CapsuleError,
  KIND_GIFT_WRAP,
  KIND_SEAL,
  KIND_TIME_CAPSULE,
  MAX_PLAINTEXT_BYTES,
  MAX_PRIVATE_PLAINTEXT_BYTES,
  MAX_ROUND,
  readCapsuleTag,
  readPublicHeader,
  readStanza,
  readCapsuleSender,
  readDiscreet,
  readIntro,
  readPayload,
  readSubject,
  readWrapHeader,
  readSase,
  readReplyTo,
  readRecipients,
  assertNoPrivateTags,
  normalizeIntro,
  normalizeSubject,
  MAX_INTRO_CHARS,
  MAX_SUBJECT_CHARS,
  MAX_RECIPIENT_TAGS,
  verifyAuthor,
  type WrapHeader,
  type CapsuleErrorCode,
  type Payload,
  type CapsuleHeader,
  type ReplyEnvelope,
  type NamedKey,
  type Recipients,
} from "./validate.js";

/** `plaintext` is the text; `files` the manifest's file entries (empty for v1 capsules and text-only ones). */
export type OpenedCapsule = CapsuleHeader & { plaintext: string; files: ManifestFile[] };

export type SealOptions = {
  /** The text. Every capsule carries a manifest (PLAN.md 4.9): this text plus `files`. */
  plaintext: string;
  /** Entries from encryptFile, for blobs uploaded before sealing. */
  files?: ManifestFile[];
  round: number;
  secretKey: Uint8Array;
  chainClient: ChainClient;
  /** Optional human-readable hint stored in the "alt" tag. Not encrypted. */
  alt?: string;
  /** The author's profile now, signed into the capsule as "sender" tags (PLAN.md 4.10). */
  sender?: SenderSnapshot;
  /**
   * Required, never encrypted (PLAN.md 4.14): a "subject" tag on the public capsule, and on a private
   * one both inside (signed by the author) and on the wrap, where the recipient sees it before
   * opening anything. Normalized to one line, 1 to 200 characters.
   */
  subject: string;
  /**
   * Optional teaser readable before unlock (PLAN.md 4.17), up to 200 characters, line breaks kept.
   * Public: a plaintext "intro" tag. Private: an "intro" tag in the inner capsule, encrypted to the
   * recipient with the seal; the wrap does not show it. Empty after normalizing means no tag.
   */
  intro?: string;
  /**
   * Private capsules only (PLAN.md 4.16 K10): a bare ["discreet"] tag in the inner capsule asks the
   * recipient's lists to hide the sender's name until the capsule is clicked. Encrypted to the
   * recipient with the rest of the seal; the wrap does not show it.
   */
  discreet?: boolean;
  /**
   * Reply envelope (PLAN.md 4.21), private capsules only: ask the recipient for a private reply back
   * to the author. `round` is the round the reply must unlock at; null lets the recipient choose.
   * Written in the inner capsule (signed) and on the wrap (the server sees it).
   */
  sase?: { round: number | null };
  /** Reply (PLAN.md 4.21), private capsules only: the id of the wrap this capsule answers. In the inner capsule and on the wrap. */
  replyTo?: string;
  /**
   * CC (PLAN.md 4.21), private capsules only: who receives this capsule, as `to` and `cc` tags in the
   * inner capsule (the wrap does not show them). The keys must be exactly the wraps being made, so
   * the list can be trusted as far as the author's signature goes.
   */
  recipients?: { to: NamedKey[]; cc: NamedKey[] };
};

const utf8 = new TextEncoder();
const utf8Decoder = new TextDecoder("utf-8", { fatal: true, ignoreBOM: false });

/** The manifest to time-lock; its size, not the text's, is what the limits measure. */
function manifestFor(opts: SealOptions, limit: number = MAX_PLAINTEXT_BYTES): string {
  const files = opts.files ?? [];
  if (files.length > MAX_FILES) throw new CapsuleError("PLAINTEXT_TOO_LARGE", `at most ${MAX_FILES} files per capsule`);
  if (utf8.encode(opts.plaintext).length === 0 && files.length === 0) throw new CapsuleError("PLAINTEXT_TOO_LARGE", "message is empty");
  const manifest = encodeManifest(opts.plaintext, files);
  const size = utf8.encode(manifest).length;
  if (size > limit) throw new CapsuleError("PLAINTEXT_TOO_LARGE", `message is ${size} bytes, limit is ${limit}`);
  return manifest;
}

/** Decrypted bytes -> text and files, by the capsule's payload tag. */
function readContent(decrypted: string, payload: Payload): { plaintext: string; files: ManifestFile[] } {
  if (payload === "text") return { plaintext: decrypted, files: [] };
  try {
    const m = parseManifest(decrypted);
    return { plaintext: m.text, files: m.files };
  } catch (err) {
    throw new CapsuleError("INVALID_CAPSULE", err instanceof ManifestError ? err.message : String(err));
  }
}

/** age binary -> base64 as NIP-XX requires (not ASCII armor). */
async function encryptToRound(plaintext: string, round: number, chainClient: ChainClient): Promise<string> {
  const armored = await timelockEncrypt(round, Buffer.from(plaintext, "utf8"), chainClient);
  const binary = decodeArmor(armored);
  return base64.encode(Buffer.from(binary, "binary"));
}

async function decryptFromRound(contentBase64: string, chainClient: ChainClient): Promise<string> {
  let binary: string;
  try {
    binary = Buffer.from(base64.decode(contentBase64)).toString("binary");
  } catch {
    throw new CapsuleError("INVALID_CAPSULE", "content is not valid base64");
  }
  let bytes: Uint8Array;
  try {
    bytes = await timelockDecrypt(encodeArmor(binary), chainClient);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    if (/not.*(yet|exist|found)|404|too early/i.test(msg)) {
      throw new CapsuleError("ROUND_NOT_REACHED", `beacon for this round is not available yet: ${msg}`);
    }
    throw new CapsuleError("BEACON_UNAVAILABLE", `could not fetch or verify the beacon: ${msg}`);
  }
  try {
    return utf8Decoder.decode(bytes);
  } catch {
    throw new CapsuleError("INVALID_CAPSULE", "decrypted bytes are not valid UTF-8");
  }
}

function tlockTag(round: number): string[] {
  return ["tlock", QUICKNET_CHAIN_HASH, String(round)];
}

/**
 * Public capsule: anyone can read it once the round exists.
 * Returns a signed kind 1041 event ready to be stored or relayed.
 */
export async function sealPublic(opts: SealOptions): Promise<VerifiedEvent> {
  if (opts.discreet) throw new CapsuleError("INVALID_CAPSULE", "only a private capsule can be discreet");
  if (opts.sase) throw new CapsuleError("INVALID_CAPSULE", "only a private capsule can ask for a reply");
  if (opts.replyTo) throw new CapsuleError("INVALID_CAPSULE", "only a private capsule can be a reply");
  if (opts.recipients) throw new CapsuleError("INVALID_CAPSULE", "only a private capsule names its recipients");
  const manifest = manifestFor(opts);
  assertRound(opts.round);
  const content = await encryptToRound(manifest, opts.round, opts.chainClient);
  const tags = [tlockTag(opts.round), ["payload", PAYLOAD_MANIFEST_V1], ["subject", sealSubject(opts.subject)]];
  const intro = sealIntro(opts.intro);
  if (intro) tags.push(["intro", intro]);
  if (opts.sender) tags.push(...senderTags(opts.sender));
  if (opts.alt) tags.push(["alt", opts.alt]);
  return finalizeEvent(
    { kind: KIND_TIME_CAPSULE, created_at: Math.floor(Date.now() / 1000), tags, content },
    opts.secretKey,
  );
}

/** Opens a public capsule. Fails with ROUND_NOT_REACHED before the unlock time. */
export async function openPublic(event: Event, chainClient: ChainClient, nowMs: number = Date.now()): Promise<OpenedCapsule> {
  const header = readPublicHeader(event);
  if (header.round > currentRound(nowMs)) {
    throw new CapsuleError("ROUND_NOT_REACHED", `round ${header.round} unlocks at ${new Date(header.unlockAt).toISOString()}`);
  }
  const decrypted = await decryptFromRound(event.content, chainClient);
  const { plaintext, files } = readContent(decrypted, header.payload);
  return { ...header, plaintext, files };
}

/**
 * Private capsule, NIP-XX / NIP-59 layout: rumor(1041) -> seal(13) -> wrap(1059).
 * Only `recipientPublicKey` can open the wrap; the text still needs the drand round.
 * Returns the kind 1059 wrap. The server sees the recipient and nothing else.
 */
export async function sealPrivate(opts: SealOptions & { recipientPublicKey: string }): Promise<VerifiedEvent> {
  assertRecipientList(opts, [opts.recipientPublicKey]);
  const { rumor, signer } = await privateRumor(opts);
  return wrapFor(rumor, signer, opts.recipientPublicKey, outerTags(opts));
}

/**
 * Like sealPrivate, plus a second wrap of the same inner capsule addressed to the author,
 * so the author can read it after unlock too (NIP-17 style self copy). Both wraps use the
 * standard 1059 -> 13 -> 1041 layout; the time lock and rumor id are identical.
 */
export async function sealPrivateWithCopy(
  opts: SealOptions & { recipientPublicKey: string },
): Promise<{ wrap: VerifiedEvent; copy: VerifiedEvent }> {
  assertRecipientList(opts, [opts.recipientPublicKey]);
  const { rumor, signer } = await privateRumor(opts);
  const outer = outerTags(opts);
  return {
    wrap: wrapFor(rumor, signer, opts.recipientPublicKey, outer),
    copy: wrapFor(rumor, signer, getPublicKey(opts.secretKey), outer),
  };
}

/**
 * One capsule to several members (PLAN.md 4.14): a group has no key, so each recipient gets a wrap
 * of their own; all carry the same subject, unlock time and manifest, so attachments are uploaded
 * once. `copy`: one more wrap to the author, of the same inner capsule.
 */
export async function sealPrivateGroup(
  opts: SealOptions & { recipientPublicKeys: string[]; copy: boolean },
): Promise<{ wraps: VerifiedEvent[]; copy: VerifiedEvent | null }> {
  const recipients = opts.recipientPublicKeys;
  if (recipients.length === 0 || new Set(recipients).size !== recipients.length) {
    throw new CapsuleError("INVALID_RECIPIENT", "a group send needs distinct recipients");
  }
  assertRecipientList(opts, recipients);
  const outer = outerTags(opts);
  const { rumor, signer } = await privateRumor({ ...opts, recipientPublicKey: recipients[0] });
  const wraps = recipients.map((r) => wrapFor(rumor, signer, r, outer));
  const copy = opts.copy ? wrapFor(rumor, signer, getPublicKey(opts.secretKey), outer) : null;
  return { wraps, copy };
}

function sealSubject(subject: string): string {
  return assertSubject(normalizeSubject(subject ?? ""));
}

function sealIntro(intro: string | undefined): string | null {
  const value = normalizeIntro(intro ?? "");
  return value === "" ? null : assertIntro(value);
}

/**
 * Plaintext tags on the wrap besides "p" (PLAN.md 4.14): subject and unlock time, readable without a
 * key; since PLAN.md 4.21 also the reply envelope and the reply link, which the server checks.
 */
function outerTags(opts: SealOptions): string[][] {
  return wrapTags(sealSubject(opts.subject), opts.round, sealSase(opts), sealReplyTo(opts));
}

function wrapTags(subject: string, round: number, sase: ReplyEnvelope | null, replyTo: string | null): string[][] {
  return [["subject", subject], ["unlock", String(Math.floor(timeForRound(round) / 1000))], ...envelopeTags(sase, replyTo)];
}

/** The "sase" and "reply" tags (PLAN.md 4.21), the same in the inner capsule and on the wrap. */
function envelopeTags(sase: ReplyEnvelope | null, replyTo: string | null): string[][] {
  const tags: string[][] = [];
  if (sase) tags.push(sase.unlockAt === null ? ["sase"] : ["sase", String(Math.floor(sase.unlockAt / 1000))]);
  if (replyTo) tags.push(["reply", replyTo]);
  return tags;
}

function sealSase(opts: SealOptions): ReplyEnvelope | null {
  if (!opts.sase) return null;
  if (opts.sase.round === null) return { unlockAt: null };
  assertRound(opts.sase.round);
  return { unlockAt: timeForRound(opts.sase.round) };
}

function sealReplyTo(opts: SealOptions): string | null {
  if (opts.replyTo === undefined) return null;
  return readReplyTo({ tags: [["reply", opts.replyTo]] });
}

/** The "to" and "cc" tags of the inner capsule, validated as a reader would (PLAN.md 4.21). */
function recipientTags(opts: SealOptions): string[][] {
  if (!opts.recipients) return [];
  const tags = [
    ...opts.recipients.to.map((r) => ["to", r.pubkey, r.display_name]),
    ...opts.recipients.cc.map((r) => ["cc", r.pubkey, r.display_name]),
  ];
  readRecipients({ tags });
  return tags;
}

/**
 * A named list may only describe the wraps actually made: the keys in `to` and `cc` must be exactly
 * the recipients, so a recipient can trust the list as far as the author's signature goes.
 */
function assertRecipientList(opts: SealOptions, recipientPublicKeys: string[]): void {
  if (!opts.recipients) return;
  const listed = [...opts.recipients.to, ...opts.recipients.cc].map((r) => r.pubkey);
  const wanted = new Set(recipientPublicKeys);
  if (listed.length !== wanted.size || listed.some((k) => !wanted.has(k))) {
    throw new CapsuleError("INVALID_RECIPIENT", "the to and cc lists must name exactly the recipients of this send");
  }
}

/** The inner capsule, and the author's key that signs it and the seal. */
async function privateRumor(opts: SealOptions & { recipientPublicKey: string }) {
  assertRound(opts.round);
  if (!isValidPublicKey(opts.recipientPublicKey)) {
    throw new CapsuleError("INVALID_RECIPIENT", "recipient public key must be 64 hex characters");
  }
  const manifest = manifestFor(opts, MAX_PRIVATE_PLAINTEXT_BYTES);
  const content = await encryptToRound(manifest, opts.round, opts.chainClient);
  const tags = [tlockTag(opts.round), ["payload", PAYLOAD_MANIFEST_V1], ["subject", sealSubject(opts.subject)]];
  const intro = sealIntro(opts.intro);
  if (intro) tags.push(["intro", intro]);
  if (opts.sender) tags.push(...senderTags(opts.sender));
  tags.push(...recipientTags(opts));
  if (opts.discreet) tags.push(["discreet"]);
  tags.push(...envelopeTags(sealSase(opts), sealReplyTo(opts)));
  if (opts.alt) tags.push(["alt", opts.alt]);
  return { rumor: createRumor({ kind: KIND_TIME_CAPSULE, tags, content }, opts.secretKey), signer: opts.secretKey };
}

/**
 * NIP-59 gift wrap, as nostr-tools' createWrap (one-time key, NIP-44 to the recipient, created_at
 * up to two days back), plus our plaintext tags after "p". unwrapEvent reads it unchanged.
 */
function wrapFor(rumor: ReturnType<typeof createRumor>, secretKey: Uint8Array, recipientPublicKey: string, extraTags: string[][]): VerifiedEvent {
  const seal = createSeal(rumor, secretKey, recipientPublicKey);
  const oneTime = generateSecretKey();
  const content = nip44Encrypt(JSON.stringify(seal), getConversationKey(oneTime, recipientPublicKey));
  const createdAt = Math.round(Date.now() / 1000 - Math.random() * TWO_DAYS);
  return finalizeEvent({ kind: KIND_GIFT_WRAP, content, created_at: createdAt, tags: [["p", recipientPublicKey], ...extraTags] }, oneTime);
}

const TWO_DAYS = 2 * 24 * 60 * 60;

export type PrivateHeader = CapsuleHeader & { rumor: Event };

/**
 * Removes the recipient layer and returns the inner capsule header.
 * Possible immediately on receipt: the recipient learns author and unlock time,
 * but not the text. Fails with NOT_RECIPIENT for anyone else.
 */
export function unwrapPrivate(wrap: Event, recipientSecretKey: Uint8Array): PrivateHeader {
  if (wrap.kind !== KIND_GIFT_WRAP) {
    throw new CapsuleError("INVALID_CAPSULE", `expected kind ${KIND_GIFT_WRAP}, got ${wrap.kind}`);
  }
  const me = getPublicKey(recipientSecretKey);
  const pTags = wrap.tags.filter((t) => t[0] === "p");
  if (pTags.length !== 1 || pTags[0][1] !== me) {
    throw new CapsuleError("NOT_RECIPIENT", "this capsule is addressed to someone else");
  }
  // The outer wrap is signed by a one-time key, so its signature proves no author; it proves the
  // wrap (recipient tag, content) is the event that was signed and stored (PLAN.md 4.13 R8). Checked
  // on a plain copy, like every signature here.
  verifyAuthor(wrap);
  let rumor: ReturnType<typeof unwrapEvent>;
  try {
    rumor = unwrapEvent(wrap, recipientSecretKey);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    if (/signature/i.test(msg)) throw new CapsuleError("INVALID_SIGNATURE", msg);
    if (/pubkey.*does not match/i.test(msg)) throw new CapsuleError("INVALID_SIGNATURE", msg);
    throw new CapsuleError("NOT_RECIPIENT", `cannot open wrap: ${msg}`);
  }
  if (rumor.tags.some((t) => t[0] === "p")) {
    throw new CapsuleError("INVALID_CAPSULE", "inner capsule must not carry p tags");
  }
  const { chainHash, round } = readCapsuleTag(rumor);
  const subject = readSubject(rumor);
  const sender = readCapsuleSender(rumor);
  const sase = readSase(rumor);
  const replyTo = readReplyTo(rumor);
  // The wrap's plaintext tags are the sender's word only; they must match the signed inner capsule.
  const outer = readWrapHeader(wrap);
  if (outer.subject !== subject) throw new CapsuleError("INVALID_CAPSULE", "the wrap's subject differs from the signed one");
  if (outer.unlockAt !== null && outer.unlockAt !== timeForRound(round)) {
    throw new CapsuleError("INVALID_CAPSULE", "the wrap's unlock time differs from the time lock");
  }
  const sameSase = outer.sase === null ? sase === null : sase !== null && outer.sase.unlockAt === sase.unlockAt;
  if (!sameSase) throw new CapsuleError("INVALID_CAPSULE", "the wrap's reply envelope differs from the signed one");
  if (outer.replyTo !== replyTo) throw new CapsuleError("INVALID_CAPSULE", "the wrap's reply link differs from the signed one");
  return {
    author: rumor.pubkey,
    chainHash,
    round,
    unlockAt: timeForRound(round),
    createdAt: rumor.created_at,
    sender,
    payload: readPayload(rumor),
    subject,
    intro: readIntro(rumor),
    discreet: readDiscreet(rumor),
    sase,
    replyTo,
    recipients: readRecipients(rumor),
    rumor: rumor as Event,
  };
}

/**
 * "Sender approves" (PLAN.md 4.14): the author opens their own copy and wraps the same inner capsule
 * to a member who did not exist when it was written. Same rumor, same time lock, same subject,
 * unlock time and reply envelope (the wrap's tags are rebuilt from the signed inner capsule); the
 * seal is signed by the author again, so authorship stays provable.
 */
export function rewrapPrivate(copy: Event, authorSecretKey: Uint8Array, recipientPublicKey: string): VerifiedEvent {
  if (!isValidPublicKey(recipientPublicKey)) throw new CapsuleError("INVALID_RECIPIENT", "recipient public key must be 64 hex characters");
  const header = unwrapPrivate(copy, authorSecretKey);
  if (header.author !== getPublicKey(authorSecretKey)) {
    throw new CapsuleError("INVALID_CAPSULE", "only the author's own, signed capsule can be passed on");
  }
  const tags = wrapTags(header.subject ?? "", header.round, header.sase, header.replyTo).filter((t) => t[0] !== "subject" || t[1] !== "");
  return wrapFor(header.rumor as ReturnType<typeof createRumor>, authorSecretKey, recipientPublicKey, tags);
}

/** Opens a private capsule: recipient layer, then time lock. */
export async function openPrivate(
  wrap: Event,
  recipientSecretKey: Uint8Array,
  chainClient: ChainClient,
  nowMs: number = Date.now(),
): Promise<OpenedCapsule> {
  const header = unwrapPrivate(wrap, recipientSecretKey);
  if (header.round > currentRound(nowMs)) {
    throw new CapsuleError("ROUND_NOT_REACHED", `round ${header.round} unlocks at ${new Date(header.unlockAt).toISOString()}`);
  }
  const decrypted = await decryptFromRound(header.rumor.content, chainClient);
  const { rumor: _rumor, ...rest } = header;
  const { plaintext, files } = readContent(decrypted, header.payload);
  return { ...rest, plaintext, files };
}
